'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function WindChillCalculator() {
  const t = useTranslations("tools.windChillCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    windSpeed: 15,
    windSpeedUnit: 'mph',
    airTemp: 10,
    airTempUnit: 'fahrenheit'
  });

  const [results, setResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Conversion functions
  const convertToMph = (speed: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'mph': 1,
      'kmh': 0.621371,
      'ms': 2.23694,
      'knots': 1.15078
    };
    return speed * conversions[unit];
  };

  const convertToFahrenheit = (temp: number, unit: string): number => {
    if (unit === 'fahrenheit') return temp;
    if (unit === 'celsius') return (temp * 9/5) + 32;
    if (unit === 'kelvin') return (temp - 273.15) * 9/5 + 32;
    return temp;
  };

  const convertFromFahrenheit = (tempF: number, unit: string): number => {
    if (unit === 'fahrenheit') return tempF;
    if (unit === 'celsius') return (tempF - 32) * 5/9;
    if (unit === 'kelvin') return (tempF - 32) * 5/9 + 273.15;
    return tempF;
  };

  const calculateWindChill = (tempF: number, windMph: number): number | null => {
    if (tempF > 50) {
      return null;
    }

    if (windMph < 3) {
      return tempF;
    }

    const windChill = 35.74 + (0.6215 * tempF) - (35.75 * Math.pow(windMph, 0.16)) + (0.4275 * tempF * Math.pow(windMph, 0.16));
    return windChill;
  };

  const getFrostbiteTime = (windChillF: number): string | null => {
    if (windChillF > 16) return null;
    if (windChillF > -19) return '30 minutes';
    if (windChillF > -35) return '10 minutes';
    if (windChillF > -48) return '5 minutes';
    return '2 minutes or less';
  };

  const getWarningLevel = (windChillF: number, tempF: number) => {
    if (tempF > 50) {
      return {
        type: 'info',
        message: t('warningInfo')
      };
    }

    if (windChillF > 32) {
      return {
        type: 'safe',
        message: t('warningSafe')
      };
    }

    if (windChillF > 15) {
      return {
        type: 'caution',
        message: t('warningCaution')
      };
    }

    if (windChillF > -15) {
      return {
        type: 'warning',
        message: t('warningModerate')
      };
    }

    if (windChillF > -50) {
      return {
        type: 'danger',
        message: t('warningDanger')
      };
    }

    return {
      type: 'extreme',
      message: t('warningExtreme')
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const windMph = convertToMph(inputs.windSpeed, inputs.windSpeedUnit);
    const tempF = convertToFahrenheit(inputs.airTemp, inputs.airTempUnit);

    const windChillF = calculateWindChill(tempF, windMph);
    const windChillConverted = convertFromFahrenheit(windChillF !== null ? windChillF : tempF, inputs.airTempUnit);

    const tempDiff = windChillF !== null ? Math.abs(tempF - windChillF) : 0;
    const tempDiffConverted = inputs.airTempUnit === 'celsius' ? tempDiff * 5/9 : (inputs.airTempUnit === 'kelvin' ? tempDiff * 5/9 : tempDiff);

    const warning = getWarningLevel(windChillF !== null ? windChillF : tempF, tempF);
    const frostbiteTime = getFrostbiteTime(windChillF !== null ? windChillF : tempF);

    setResults({
      windChill: windChillConverted,
      actualTemp: inputs.airTemp,
      windSpeed: inputs.windSpeed,
      tempDiff: tempDiffConverted,
      warning,
      frostbiteTime
    });

    setShowResults(true);
  };

  const handleClear = () => {
    setInputs({
      windSpeed: 15,
      windSpeedUnit: 'mph',
      airTemp: 10,
      airTempUnit: 'fahrenheit'
    });
    setShowResults(false);
    setResults(null);
  };

  const getUnitSymbol = (unit: string): string => {
    if (unit === 'fahrenheit') return '°F';
    if (unit === 'celsius') return '°C';
    if (unit === 'kelvin') return 'K';
    return '';
  };

  const getSpeedSymbol = (unit: string): string => {
    if (unit === 'mph') return 'mph';
    if (unit === 'kmh') return 'km/h';
    if (unit === 'ms') return 'm/s';
    if (unit === 'knots') return 'knots';
    return '';
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 50%, #e0e7ff 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', margin: '0 0 0.5rem 0' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: '#475569', margin: 0 },
    { fontSize: '0.9rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#1e293b', marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputWrapperStyle = {
    display: 'flex',
    gap: '0.5rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      outline: 'none'
    },
    { padding: '0.75rem', fontSize: '1rem', minWidth: '120px' },
    { padding: '0.75rem', fontSize: '1rem', minWidth: '140px' },
    { padding: '0.75rem', fontSize: '1rem', minWidth: '140px' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      animation: 'slideIn 0.4s ease'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      textAlign: 'center' as const
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#475569', marginBottom: '0.5rem', fontWeight: 500 },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e40af' },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle = {
    background: '#f8fafc',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #2563eb'
  };

  const resultItemLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.25rem'
  };

  const resultItemValueStyle = {
    display: 'block',
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#1e293b'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    color: colors.textSecondary,
    lineHeight: 1.8,
    paddingLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.4)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const,
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>❄️ {t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGridStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="wind-speed" style={labelStyle}>{t('windSpeedLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="wind-speed"
                    step="0.1"
                    min="0"
                    value={inputs.windSpeed}
                    onChange={(e) => setInputs({...inputs, windSpeed: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    id="wind-speed-unit"
                    value={inputs.windSpeedUnit}
                    onChange={(e) => setInputs({...inputs, windSpeedUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="mph">{t('unitMph')}</option>
                    <option value="kmh">{t('unitKmh')}</option>
                    <option value="ms">{t('unitMs')}</option>
                    <option value="knots">{t('unitKnots')}</option>
                  </select>
                </div>
              </div>

              <div style={formGroupStyle}>
                <label htmlFor="air-temp" style={labelStyle}>{t('airTempLabel')}</label>
                <div style={inputWrapperStyle}>
                  <input
                    type="number"
                    id="air-temp"
                    step="0.1"
                    value={inputs.airTemp}
                    onChange={(e) => setInputs({...inputs, airTemp: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    id="air-temp-unit"
                    value={inputs.airTempUnit}
                    onChange={(e) => setInputs({...inputs, airTempUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="fahrenheit">{t('unitFahrenheit')}</option>
                    <option value="celsius">{t('unitCelsius')}</option>
                    <option value="kelvin">{t('unitKelvin')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearButtonStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {showResults && results && (
            <div style={resultsStyle}>
              <h3 style={{...h2Style, textAlign: 'center' as const, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem'}}>
                {t('resultsTitle')}
              </h3>

              <div style={resultMainStyle}>
                <span style={resultLabelStyle}>{t('windChillTempLabel')}</span>
                <span style={resultValueStyle}>
                  {results.windChill.toFixed(1)}{getUnitSymbol(inputs.airTempUnit)}
                </span>
              </div>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('actualTempLabel')}</span>
                  <span style={resultItemValueStyle}>
                    {results.actualTemp.toFixed(1)}{getUnitSymbol(inputs.airTempUnit)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('windSpeedDisplayLabel')}</span>
                  <span style={resultItemValueStyle}>
                    {results.windSpeed.toFixed(1)} {getSpeedSymbol(inputs.windSpeedUnit)}
                  </span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultItemLabelStyle}>{t('tempDiffLabel')}</span>
                  <span style={resultItemValueStyle}>
                    {results.tempDiff.toFixed(1)}° {t('colder')}
                  </span>
                </div>
              </div>

              {results.warning && (
                <div style={{
                  background: results.warning.type === 'danger' || results.warning.type === 'extreme' ? '#fee2e2' :
                             results.warning.type === 'safe' ? '#d1fae5' : '#fef3c7',
                  borderLeft: `4px solid ${results.warning.type === 'danger' || results.warning.type === 'extreme' ? '#dc2626' :
                                          results.warning.type === 'safe' ? '#10b981' : '#f59e0b'}`,
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginTop: '1rem'
                }}>
                  <p style={{
                    margin: 0,
                    color: results.warning.type === 'danger' || results.warning.type === 'extreme' ? '#7f1d1d' :
                           results.warning.type === 'safe' ? '#065f46' : '#78350f',
                    fontSize: '0.9rem',
                    lineHeight: 1.5
                  }}>
                    <strong style={{ display: 'block', marginBottom: '0.25rem', fontSize: '1rem' }}>
                      {results.warning.type === 'extreme' ? '🚨 ' :
                       results.warning.type === 'danger' ? '⚠️ ' :
                       results.warning.type === 'safe' ? '✓ ' :
                       results.warning.type === 'info' ? 'ℹ️ ' : '⚠️ '}
                      {results.warning.type === 'extreme' ? t('extremeDanger') :
                       results.warning.type === 'danger' ? t('danger') :
                       results.warning.type === 'warning' ? t('warning') :
                       results.warning.type === 'safe' ? t('safeConditions') :
                       results.warning.type === 'caution' ? t('caution') : t('note')}
                    </strong>
                    {results.warning.message}
                    {results.frostbiteTime && (
                      <>
                        <br />
                        <strong>{t('frostbiteTime')}</strong> {results.frostbiteTime}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightBullet1')}</li>
          <li style={listItemStyle}>{t('insightBullet2')}</li>
          <li style={listItemStyle}>{t('insightBullet3')}</li>
          <li style={listItemStyle}>{t('insightBullet4')}</li>
        </ul>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t('formulaTitle')}
        </h3>
        <p style={paragraphStyle}>{t('formulaParagraph1')}</p>
        <p style={{...paragraphStyle, fontFamily: 'monospace', background: '#f8fafc', padding: '1rem', borderRadius: '0.5rem', overflowX: 'auto'}}>
          T<sub>wc</sub> = 35.74 + 0.6215T<sub>a</sub> - 35.75V<sup>0.16</sup> + 0.4275T<sub>a</sub>V<sup>0.16</sup>
        </p>
        <p style={paragraphStyle}>{t('formulaParagraph2')}</p>
        <p style={{...paragraphStyle, fontFamily: 'monospace', background: '#f8fafc', padding: '1rem', borderRadius: '0.5rem', overflowX: 'auto'}}>
          T<sub>wc</sub> = 13.12 + 0.6215T<sub>a</sub> - 11.37V<sup>0.16</sup> + 0.3965T<sub>a</sub>V<sup>0.16</sup>
        </p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t('validityTitle')}
        </h3>
        <p style={paragraphStyle}>{t('validityParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('validityBullet1')}</li>
          <li style={listItemStyle}>{t('validityBullet2')}</li>
        </ul>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem'}}>
          {t('healthRisksTitle')}
        </h3>
        <p style={paragraphStyle}>{t('healthRisksParagraph')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('healthRiskBullet1')}</li>
          <li style={listItemStyle}>{t('healthRiskBullet2')}</li>
          <li style={listItemStyle}>{t('healthRiskBullet3')}</li>
          <li style={listItemStyle}>{t('healthRiskBullet4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://en.wikipedia.org/wiki/Wind_chill"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.weather.gov/safety/cold-wind-chill-chart"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.canada.ca/en/environment-climate-change/services/weather-health/wind-chill-cold-weather.html"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.metoffice.gov.uk/weather/learn-about/weather/types-of-weather/wind/windchill"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
            >
              {t('reference4')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}